import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.ts";
import type { DepositPaymentPage, DepositPaymentRow, DepositReviewRow, Filters } from "../lib/api.ts";
import { money, num } from "../lib/format.ts";
import { plural, t, type L } from "../i18n/lang.ts";
import { UI } from "../i18n/dict.ts";
import { Card, DrillNum } from "./widgets.tsx";
import { Th, useSortable } from "./sortable.tsx";
import { DrawerActions } from "./exportButton.tsx";
import { exportToExcel, fetchAllPages, moneyValue, type ExportColumn } from "../lib/tableExport.ts";

/**
 * Блок «Deposits to fix» экрана Pipeline conversion и список оплат под ним.
 *
 * С 12.10.2026 закрытый депозит подтверждается ОПЛАТОЙ в системе бронирования
 * (миграция 097). Всё, что между сделкой и оплатой не сошлось, мы не переносим
 * и не угадываем — решение пользователя: «такие сделки нужно отображать прям в
 * том же разделе… чтобы клиент сам мог исправлять ошибки свои». Каждая строка
 * блока — список с переходом в Битрикс и выгрузкой в Excel.
 *
 * Строки-сделки открывают обычный список сделок (`DealDrawer`) с тем же срезом,
 * которым сервер посчитал число; строки-оплаты — этот файл, `PaymentDrawer`.
 */

const TX = {
  title: { ru: "Депозиты на исправление", en: "Deposits to fix" },
  hint: {
    ru: "Всё, что не сошлось между сделкой и оплатой в системе бронирования. Исправляется в CRM или в системе бронирования; после исправления строка уходит сама",
    en: "Everything that does not match between the deal and the payment in the booking system. Fixed in the CRM or in the booking system; a row disappears once it is fixed",
  },
  colWhat: { ru: "Что не так", en: "What is wrong" },
  colCount: { ru: "Сколько", en: "Count" },
  colAmount: { ru: "Депозит, AED", en: "Deposit, AED" },
  colCounted: { ru: "В Closed deposits", en: "In Closed deposits" },
  colFix: { ru: "Что сделать", en: "How to fix" },
  yes: { ru: "да", en: "yes" },
  no: { ru: "нет", en: "no" },
  allClear: { ru: "За период всё сходится", en: "Everything matches for the period" },
  note: {
    ru: "Период — когда сделка вошла в оборот депозита: день оплаты, иначе вход в Booked, иначе создание; у оплат — день оплаты. Закрытый депозит с 01.09.2026 — это оплата в системе бронирования, привязанная к сделке; сделки без неё в плитку не входят, пока их не исправят.",
    en: "Period: when the deal entered the deposit flow — the payment day, else the move to Booked, else creation; for payments, the payment day. Since 1 Sep 2026 a closed deposit is a payment in the booking system linked to the deal; deals without one are not counted until they are fixed.",
  },
  /* --- панель оплат */
  colPaid: { ru: "Оплачено", en: "Paid" },
  colPark: { ru: "Парк", en: "Park" },
  colMethod: { ru: "Способ", en: "Method" },
  colManager: { ru: "Менеджер брони", en: "Booking manager" },
  colParty: { ru: "Праздник", en: "Party" },
  colWhere: { ru: "Где висит оплата", en: "Where the payment sits" },
  lead: { ru: "лид", en: "lead" },
  leadDeals: { ru: "сделки лида:", en: "lead's deals:" },
  noLeadDeals: { ru: "сделки у лида нет", en: "the lead has no deal" },
  deal: { ru: "сделка", en: "deal" },
  dealDeleted: { ru: "удалена в Битриксе", en: "deleted in Bitrix" },
  notLinked: { ru: "ни к чему не привязана", en: "not linked to anything" },
  noPayments: { ru: "Оплат нет", en: "No payments" },
  paymentsCounted: {
    one: { ru: "оплата", en: "payment" },
    few: { ru: "оплаты", en: "payments" },
    many: { ru: "оплат", en: "payments" },
  },
  totalling: { ru: "на сумму", en: "totalling" },
  exRef: { ru: "Бронь", en: "Booking" },
  exAmountNative: { ru: "Сумма в валюте парка", en: "Amount in park currency" },
  exCurrency: { ru: "Валюта", en: "Currency" },
  exLeadId: { ru: "ID лида", en: "Lead ID" },
  exLeadLink: { ru: "Ссылка на лид", en: "Lead link" },
  exLeadDeals: { ru: "Сделки лида", en: "Lead's deals" },
  exDealId: { ru: "ID сделки", en: "Deal ID" },
  exDealDeleted: { ru: "Сделка удалена", en: "Deal deleted" },
  exDealLink: { ru: "Ссылка на сделку", en: "Deal link" },
  firstPage: { ru: "⇤ В начало", en: "⇤ First" },
  previousPage: { ru: "← Назад", en: "← Previous" },
  nextPage: { ru: "Далее →", en: "Next →" },
} satisfies Record<string, L | { one: L; few: L; many: L }>;

/** Подпись строки и что с ней делать. Ключ приезжает с сервера. */
const ROW: Record<DepositReviewRow["key"], { label: L; fix: L }> = {
  deposit_unconfirmed: {
    label: {
      ru: "В Booked или Won, но оплаты в системе бронирования нет",
      en: "Booked or won, but no payment in the booking system",
    },
    fix: {
      ru: "Если деньги взяли — зарегистрировать оплату на этой сделке в системе бронирования; если нет — вернуть сделку на верную стадию",
      en: "If the money was taken, register the payment on this deal in the booking system; if not, move the deal to the right stage",
    },
  },
  deposit_paid_wrong_stage: {
    label: {
      ru: "Оплачено, а сделка в Interested, Waiting for payment или Lost",
      en: "Paid, but the deal is in Interested, Waiting for payment or Lost",
    },
    fix: {
      ru: "Перевести сделку в Booked; если деньги возвращают — в «Deal lost, a refund is required»",
      en: "Move the deal to Booked; if the money is refunded, to “Deal lost, a refund is required”",
    },
  },
  duplicate_open: {
    label: { ru: "Дубль: вторая сделка того же праздника", en: "Duplicate: a second deal for the same party" },
    fix: {
      ru: "Закрыть как Lost или удалить; основная сделка — в списке. В продажах дубль уже не считается",
      en: "Close as Lost or delete; the main deal is in the list. The duplicate is already excluded from sales",
    },
  },
  park_from_booking: {
    label: { ru: "В сделке пустой парк (взят из брони)", en: "The deal has no park (taken from the booking)" },
    fix: { ru: "Проставить парк в сделке", en: "Set the park on the deal" },
  },
  on_lead: {
    label: { ru: "Оплата осталась на лиде, а не на сделке", en: "The payment is on the lead, not on the deal" },
    fix: {
      ru: "Перепривязать бронь к сделке лида в системе бронирования (Link to deal)",
      en: "Re-link the booking to the lead's deal in the booking system (Link to deal)",
    },
  },
  without_deal: {
    label: {
      ru: "Оплата без сделки: сделку удалили или бронь не привязана",
      en: "Payment without a deal: the deal was deleted or the booking is not linked",
    },
    fix: {
      ru: "Привязать бронь к живой сделке или подтвердить возврат денег",
      en: "Link the booking to a live deal or confirm the refund",
    },
  },
};

const aed = (m: string | number | null | undefined): string => money(m, "AED", { withCode: false });

/** Что показывает список оплат. */
export type PaymentDrill = {
  title: string;
  hint?: string;
  paymentKind: "on_lead" | "without_deal";
  filters?: Partial<Filters>;
};

export function DepositReviewBlock({ rows }: { rows: DepositReviewRow[] }): React.ReactElement {
  const S = useSortable(rows, {
    what: { kind: "text", get: (r) => t(ROW[r.key].label) },
    count: { kind: "number", get: (r) => r.count },
    amount: { kind: "number", get: (r) => r.aed_minor },
  });
  const clear = rows.every((r) => r.count === 0);

  return (
    <Card title={t(TX.title)} hint={t(TX.hint)}>
      <div className="scroll-x">
        <table className="data">
          <thead>
            <tr>
              <Th sort={S} col="what">{t(TX.colWhat)}</Th>
              <Th sort={S} col="count" num>{t(TX.colCount)}</Th>
              <Th sort={S} col="amount" num>{t(TX.colAmount)}</Th>
              <th>{t(TX.colCounted)}</th>
              <th>{t(TX.colFix)}</th>
            </tr>
          </thead>
          <tbody>
            {S.rows.map((r) => (
              <tr key={r.key}>
                <td>{t(ROW[r.key].label)}</td>
                <td className="num">
                  <DrillNum
                    enabled={r.count > 0}
                    drill={
                      r.kind === "deals"
                        ? {
                            kind: "deals",
                            title: t(ROW[r.key].label),
                            hint: t(ROW[r.key].fix),
                            /* Тот же срез, которым сервер посчитал число
                               (`dealSliceTotals`): число и список совпадают. */
                            slice: {
                              dateBasis: "deposit_review",
                              issue: r.key as "deposit_unconfirmed" | "deposit_paid_wrong_stage" | "duplicate_open" | "park_from_booking",
                            },
                          }
                        : {
                            kind: "payments",
                            title: t(ROW[r.key].label),
                            hint: t(ROW[r.key].fix),
                            paymentKind: r.key as "on_lead" | "without_deal",
                          }
                    }
                  >
                    {num(r.count)}
                  </DrillNum>
                </td>
                <td className="num">{aed(r.aed_minor)}</td>
                <td>{r.counted ? t(TX.yes) : t(TX.no)}</td>
                <td className="sub">{t(ROW[r.key].fix)}</td>
              </tr>
            ))}
            {clear && (
              <tr>
                <td colSpan={5} className="empty">
                  {t(TX.allClear)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="note" style={{ marginTop: 12 }}>
        {t(TX.note)}
      </div>
    </Card>
  );
}

/** Сколько строк за раз. Потолок сервера — 500. */
const PAGE = 200;

function paymentExportColumns(): Array<ExportColumn<DepositPaymentRow>> {
  return [
    { title: t(TX.colPaid), kind: "datetime", width: 17, value: (r) => r.paid_local },
    { title: t(TX.exRef), width: 14, value: (r) => r.ref },
    { title: t(TX.colPark), width: 24, value: (r) => r.park_name },
    { title: t(TX.colAmount), kind: "money", width: 12, value: (r) => moneyValue(r.deposit_aed_minor) },
    {
      title: t(TX.exAmountNative),
      kind: "money",
      width: 14,
      value: (r) => (r.currency !== "AED" ? moneyValue(r.deposit_minor, r.currency) : null),
    },
    { title: t(TX.exCurrency), width: 8, value: (r) => r.currency },
    { title: t(TX.colMethod), width: 14, value: (r) => r.payment_method },
    { title: t(TX.colManager), width: 20, value: (r) => r.manager_name },
    { title: t(TX.colParty), kind: "datetime", width: 17, value: (r) => r.party_local },
    { title: t(TX.exLeadId), kind: "number", width: 10, value: (r) => (r.crm_lead_id === null ? null : Number(r.crm_lead_id)) },
    { title: t(TX.exLeadDeals), width: 16, value: (r) => r.lead_deals },
    { title: t(TX.exLeadLink), width: 50, value: (r) => r.lead_url },
    { title: t(TX.exDealId), kind: "number", width: 10, value: (r) => (r.crm_deal_id === null ? null : Number(r.crm_deal_id)) },
    { title: t(TX.exDealDeleted), width: 12, value: (r) => (r.deal_deleted ? t(TX.yes) : null) },
    { title: t(TX.exDealLink), width: 50, value: (r) => r.deal_url },
  ];
}

export function PaymentDrawer({
  drill,
  filters,
  onClose,
}: {
  drill: PaymentDrill | null;
  filters: Filters;
  onClose: () => void;
}): React.ReactElement | null {
  const [page, setPage] = useState<DepositPaymentPage | null>(null);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const key = drill === null ? "" : JSON.stringify([drill.paymentKind, filters]);

  useEffect(() => {
    setOffset(0);
    setPage(null);
  }, [key]);

  useEffect(() => {
    if (drill === null) return;
    let alive = true;
    setLoading(true);
    setError(null);
    api
      .depositPayments(filters, drill.paymentKind, PAGE, offset)
      .then((p) => {
        if (alive) setPage(p);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, offset]);

  useEffect(() => {
    if (drill === null) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drill, onClose]);

  const rows = page?.rows ?? [];
  const total = page?.total ?? 0;
  const S = useSortable(
    rows,
    {
      paid: { kind: "text", get: (r) => r.paid_local },
      park: { kind: "text", get: (r) => r.park_name },
      amount: { kind: "number", get: (r) => r.deposit_aed_minor },
      manager: { kind: "text", get: (r) => r.manager_name },
      party: { kind: "text", get: (r) => r.party_local },
    },
    { partial: total > rows.length },
  );

  if (drill === null) return null;

  const runExport = async (progress: (done: number, total: number) => void): Promise<void> => {
    const all =
      total <= rows.length
        ? S.rows
        : await fetchAllPages((limit, off) => api.depositPayments(filters, drill.paymentKind, limit, off), progress);
    await exportToExcel(drill.title, paymentExportColumns(), all);
  };

  const hint =
    loading && page === null
      ? t(UI.loading)
      : `${num(total)} ${plural(total, TX.paymentsCounted)} · ${t(TX.totalling)} ${money(page?.sum_aed_minor ?? 0, "AED")}` +
        (drill.hint ? ` · ${drill.hint}` : "");

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <div>
            <div className="drawer-title">{drill.title}</div>
            <div className="drawer-hint">{hint}</div>
          </div>
          <DrawerActions onClose={onClose} exportRun={runExport} exportDisabled={loading || total === 0} />
        </div>
        <div className="drawer-body">
          {error !== null && <div className="error">{error}</div>}
          {error === null && !loading && rows.length === 0 && <div className="empty">{t(TX.noPayments)}</div>}
          {rows.length > 0 && (
            <div className="scroll-x">
              <table className="data">
                <thead>
                  <tr>
                    <Th sort={S} col="paid">{t(TX.colPaid)}</Th>
                    <Th sort={S} col="park">{t(TX.colPark)}</Th>
                    <Th sort={S} col="amount" num>{t(TX.colAmount)}</Th>
                    <Th sort={S} col="manager">{t(TX.colManager)}</Th>
                    <Th sort={S} col="party">{t(TX.colParty)}</Th>
                    <th>{t(TX.colWhere)}</th>
                  </tr>
                </thead>
                <tbody>
                  {S.rows.map((r) => (
                    <tr key={r.booking_id}>
                      <td className="nowrap">
                        {r.paid_local ?? "—"}
                        <div className="sub">
                          {r.ref ?? ""}
                          {r.payment_method !== null && ` · ${r.payment_method}`}
                        </div>
                      </td>
                      <td>{r.park_name ?? "—"}</td>
                      <td className="num">
                        {aed(r.deposit_aed_minor)}
                        {r.currency !== "AED" && (
                          <div className="sub">{money(r.deposit_minor, r.currency, { withCode: true })}</div>
                        )}
                      </td>
                      <td>{r.manager_name ?? "—"}</td>
                      <td className="nowrap">{r.party_local ?? "—"}</td>
                      <td>
                        {r.crm_lead_id !== null ? (
                          <>
                            {t(TX.lead)}{" "}
                            {r.lead_url !== null ? (
                              <a href={r.lead_url} target="_blank" rel="noreferrer">
                                #{r.crm_lead_id}
                              </a>
                            ) : (
                              `#${r.crm_lead_id}`
                            )}
                            <div className="sub">
                              {r.lead_deals !== null ? `${t(TX.leadDeals)} ${r.lead_deals}` : t(TX.noLeadDeals)}
                            </div>
                          </>
                        ) : r.crm_deal_id !== null ? (
                          <>
                            {t(TX.deal)}{" "}
                            {r.deal_url !== null ? (
                              <a href={r.deal_url} target="_blank" rel="noreferrer">
                                #{r.crm_deal_id}
                              </a>
                            ) : (
                              `#${r.crm_deal_id}`
                            )}
                            {r.deal_deleted && <div className="sub">{t(TX.dealDeleted)}</div>}
                          </>
                        ) : (
                          t(TX.notLinked)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {total > rows.length && (
            <div className="drawer-foot">
              <span style={{ display: "flex", gap: 8 }}>
                <button type="button" className="btn-ghost" disabled={offset === 0 || loading} onClick={() => setOffset(0)}>
                  {t(TX.firstPage)}
                </button>
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={offset === 0 || loading}
                  onClick={() => setOffset(Math.max(0, offset - PAGE))}
                >
                  {t(TX.previousPage)}
                </button>
              </span>
              <span className="sub">
                {num(offset + 1)}–{num(offset + rows.length)} {t(UI.of)} {num(total)}
              </span>
              <button
                type="button"
                className="btn-ghost"
                disabled={offset + rows.length >= total || loading}
                onClick={() => setOffset(offset + PAGE)}
              >
                {t(TX.nextPage)}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
